/**
 * Firebase Client Initialization
 * Initializes and exports Firebase app, auth, firestore, and storage instances
 * This is the central configuration point for all Firebase services used in the app
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import env from '@/utils/env';

// Initialize Firebase app with config from environment
const app = initializeApp(env.firebase);

const databaseID = import.meta.env.VITE_FIRESTORE_DATABASE_ID || undefined;
// Initialize Firebase services
export const firebaseAuth = getAuth(app);
export const firestore = databaseID ? getFirestore(app, databaseID) : getFirestore(app);
export const storage = getStorage(app);

// Connect to emulators in development mode
// Set VITE_USE_FIREBASE_EMULATOR=true in .env to enable emulator connections
const useEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';

if (useEmulator) {
  try {
    // Connect to Auth Emulator
    connectAuthEmulator(firebaseAuth, 'http://127.0.0.1:9099', {
      disableWarnings: true
    });
    console.log('🧪 Connected to Auth Emulator (http://127.0.0.1:9099)');

    // Connect to Firestore Emulator
    connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
    console.log('🧪 Connected to Firestore Emulator (127.0.0.1:8080)');

    // Connect to Storage Emulator (if configured in firebase.json)
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    console.log('🧪 Connected to Storage Emulator (127.0.0.1:9199)');
  } catch (error) {
    // Emulator connections can only be made once, ignore if already connected
    if (error.code !== 'auth/emulator-config-failed') {
      console.warn('⚠️ Failed to connect to Firebase Emulators:', error.message);
    }
  }
} else if (import.meta.env.DEV) {
  console.log('🔥 Using real Firebase services (Auth, Firestore, Storage)');
}

// Export app instance for advanced use cases
export { app };

// Legacy exports for backward compatibility
export const auth = firebaseAuth;
